/**
 * SSOT guards for material close-flush / Uložit DELETE+PATCH.
 * Web + docs: this module. Mobile: mirror in MaterialCommitHelpers.buildMaterialFlushOps.
 *
 * Prevents:
 *  - DELETE while hydrate incomplete / in-flight (empty UI vs stale baseline)
 *  - mass-DELETE when current is empty but user did not explicitly remove rows
 *    (resetCommittedContent, failed GET, concurrent other-user rows)
 */

export type MaterialFlushSafetyInput = {
  materialUsageHydrated: boolean;
  hydrateInFlight?: boolean;
};

/** Skip DELETE/PATCH entirely until server rows are safely loaded. */
export function shouldSkipMaterialFlushMutations(input: MaterialFlushSafetyInput): boolean {
  if (input.hydrateInFlight === true) return true;
  if (!input.materialUsageHydrated) return true;
  return false;
}

export type MaterialFlushDeleteDecisionInput = {
  baselineIds: readonly string[];
  currentIds: readonly string[];
  /** Rows the user removed via UI (picker / phase modal / −). */
  explicitlyRemovedIds?: ReadonlySet<string> | readonly string[];
};

function toIdSet(ids: ReadonlySet<string> | readonly string[] | undefined): Set<string> {
  if (!ids) return new Set();
  const values = Array.isArray(ids) ? ids : Array.from(ids);
  return new Set(values.map((id) => String(id).trim()).filter(Boolean));
}

/**
 * Baseline ids that are missing from current and may be soft-deleted.
 * Mass-empty without explicit removals → none (suppress wipe).
 * Otherwise only ids the user explicitly removed (or all missing when every
 * missing id is in the explicit set — supports clearing the whole list via UI).
 */
export function materialFlushDeleteIds(input: MaterialFlushDeleteDecisionInput): string[] {
  const current = toIdSet(input.currentIds);
  const missing = input.baselineIds
    .map((id) => String(id).trim())
    .filter((id) => id.length > 0 && !current.has(id));
  if (missing.length === 0) return [];

  const explicit = toIdSet(input.explicitlyRemovedIds);

  // Empty UI + nonempty baseline: only wipe if every missing id was explicitly removed.
  if (current.size === 0) {
    if (explicit.size === 0) return [];
    const allExplicit = missing.every((id) => explicit.has(id));
    return allExplicit ? missing : [];
  }

  // Partial removals: only DELETE ids the user explicitly removed (never other users' rows
  // that vanished due to a stale/empty session merge).
  if (explicit.size === 0) return [];
  return missing.filter((id) => explicit.has(id));
}

/** Track ids present in `prev` but absent in `next` (user-driven list replace). */
export function collectRemovedMaterialUsageIds(
  prevIds: readonly string[],
  nextIds: readonly string[],
): string[] {
  const next = toIdSet(nextIds);
  return prevIds.map((id) => String(id).trim()).filter((id) => id.length > 0 && !next.has(id));
}
